import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CheckCircle, FileZip } from '@phosphor-icons/react';
import CommunicationCard from '../components/communications/CommunicationCard';
import CommunicationConfirmDialog from '../components/communications/CommunicationConfirmDialog';
import PdfPreviewModal from '../components/communications/PdfPreviewModal';

const CommunicationsPendingPage = () => {
  const { axiosInstance } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [previewProvince, setPreviewProvince] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [pecTestAddr, setPecTestAddr] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/communications/pending');
      setItems(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!previewProvince) {
      if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axiosInstance.get(`/communications/${previewProvince}/preview-pdf`, { responseType: 'blob' });
        if (!cancelled) {
          const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
          setPreviewUrl(url);
        }
      } catch (e) {
        if (!cancelled) toast.error('Errore caricamento anteprima PDF');
      }
    })();
    return () => { cancelled = true; };
  }, [previewProvince, axiosInstance]);

  const setLoader = (key, val) => setActionLoading((p) => ({ ...p, [key]: val }));

  const handleSendTestEmail = async (province) => {
    setLoader(province + '_test', true);
    try {
      const { data } = await axiosInstance.post(`/communications/${province}/send-test-email`);
      toast.success(data.message || 'Email di test inviata!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio email di test');
    } finally {
      setLoader(province + '_test', false);
      setConfirmDialog(null);
    }
  };

  const handleSendPec = async (province) => {
    setLoader(province + '_pec', true);
    try {
      const { data } = await axiosInstance.post(`/communications/${province}/send-pec`);
      toast.success(data.message || 'PEC inviata con successo!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio PEC');
    } finally {
      setLoader(province + '_pec', false);
      setConfirmDialog(null);
    }
  };

  const handleSendPecTest = async (province) => {
    if (!pecTestAddr.trim()) {
      toast.error('Inserisci un indirizzo PEC di destinazione');
      return;
    }
    setLoader(province + '_pectest', true);
    try {
      const { data } = await axiosInstance.post(`/communications/${province}/send-pec-test`, { dest_pec: pecTestAddr.trim() });
      toast.success(data.message || 'PEC di prova inviata!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio PEC di prova');
    } finally {
      setLoader(province + '_pectest', false);
      setConfirmDialog(null);
    }
  };

  const openConfirm = (type, province, customsOffice, customsPec) => {
    setConfirmDialog({ type, province, customsOffice, customsPec });
    if (type === 'pec_test') setPecTestAddr('');
  };

  const handleDownloadAll = async () => {
    setLoader('zip', true);
    try {
      const response = await axiosInstance.get('/communications/download-all', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'comunicazioni_complete.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download completato!');
    } catch (error) {
      toast.error('Errore durante il download');
    } finally {
      setLoader('zip', false);
    }
  };

  const handleRegenerate = async () => {
    setLoader('regen', true);
    try {
      await axiosInstance.post('/communications/regenerate');
      toast.success('Rigenerazione avviata...');
      setLoading(true);
      await fetchData();
      toast.success('Documenti rigenerati con i dati aggiornati!');
    } catch (error) {
      toast.error('Errore durante la rigenerazione');
    } finally {
      setLoader('regen', false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#0A2540] border-r-transparent"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="communications-pending-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tighter text-slate-900">Comunicazioni Da Inviare</h1>
          <p className="mt-2 text-sm text-slate-600">
            Documenti generati automaticamente. Puoi visualizzare l'anteprima, inviare un test o procedere con l'invio PEC definitivo.
          </p>
        </div>
        {items.length > 0 && (
          <div className="flex items-center gap-2">
            <Button
              onClick={handleRegenerate}
              disabled={actionLoading['regen']}
              variant="outline"
              className="rounded-sm shrink-0"
              data-testid="regenerate-docs"
            >
              {actionLoading['regen'] ? (
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent mr-2"></span>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
              )}
              {actionLoading['regen'] ? 'Rigenerazione...' : 'Rigenera Documenti'}
            </Button>
            <Button
              onClick={handleDownloadAll}
              disabled={actionLoading['zip']}
              className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-sm shrink-0"
              data-testid="download-all-zip"
            >
              <FileZip size={16} className="mr-2" />
              {actionLoading['zip'] ? 'Preparazione...' : 'Scarica Tutto (ZIP)'}
            </Button>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-sm p-8 text-center">
          <CheckCircle size={32} weight="fill" className="mx-auto text-green-500 mb-3" />
          <p className="text-sm text-green-800 font-medium">Nessuna comunicazione in sospeso</p>
          <p className="text-xs text-green-600 mt-1">Tutte le province attive sono state comunicate.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((item) => (
            <CommunicationCard
              key={item.province}
              item={item}
              actionLoading={actionLoading}
              onPreview={setPreviewProvince}
              onConfirm={openConfirm}
            />
          ))}
        </div>
      )}

      {confirmDialog && (
        <CommunicationConfirmDialog
          confirmDialog={confirmDialog}
          actionLoading={actionLoading}
          pecTestAddr={pecTestAddr}
          setPecTestAddr={setPecTestAddr}
          onSendTestEmail={handleSendTestEmail}
          onSendPecTest={handleSendPecTest}
          onSendPec={handleSendPec}
          onClose={() => setConfirmDialog(null)}
        />
      )}

      {previewProvince && (
        <PdfPreviewModal
          previewProvince={previewProvince}
          previewUrl={previewUrl}
          onClose={() => setPreviewProvince(null)}
        />
      )}
    </div>
  );
};

export default CommunicationsPendingPage;
